import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { TableColumn } from '@/components/common/DataTable';
import { useGetFirmsQuery } from '@/services/api/firmsService';
import { useGetTaskListMetadataQuery } from '@/services/api/tasksService';
import { Task } from '@/services/types/client-intake-types';
import { selectUser } from '@/state/selectors/root-selectors';

import { StandardColumnOptions } from '../columns/standardColumns';
import {
  type AdvancedFiltersMetadata,
  FILTER_CONFIG_KEYS,
} from '../config/filterConfig';
import { VerticalConfig } from '../config/taskVerticals';
import { getSortedOptions } from '../utils/filterUtils';

// Re-export StandardColumnOptions for consumers that import from this hook
export type { StandardColumnOptions };

// The factory type accepts StandardColumnOptions (not Partial).
// This ensures all options are available to column factories.
export type ColumnsFactory = (
  options: StandardColumnOptions,
) => TableColumn<Task>[];

interface UseTaskVerticalViewProps {
  verticalConfig: VerticalConfig;
  columnsFactory: ColumnsFactory;
}

/**
 * Custom hook to encapsulate common data fetching and preparation logic for Task Vertical views.
 */
export const useTaskVerticalView = ({
  verticalConfig,
  columnsFactory,
}: UseTaskVerticalViewProps) => {
  // 1. Fetch Data (single source of truth for all filter options)
  const { data: staticMetadata } = useGetTaskListMetadataQuery(
    verticalConfig.baseFilters,
  );

  // 2. Fetch firms to derive firm state options
  const { data: firms } = useGetFirmsQuery({});

  // 3. Get current user for "Mine" preset in Assignee filter
  const currentUser = useSelector(selectUser);

  // 4. Derive firm state options from firms data
  const firmStateOptions = useMemo(() => {
    if (!firms) return [];
    const uniqueStates = new Set<string>();
    firms.forEach((firm) => {
      if (firm.primary_state) {
        uniqueStates.add(firm.primary_state);
      }
    });
    return Array.from(uniqueStates)
      .sort()
      .map((state) => ({
        label: state,
        value: state,
      }));
  }, [firms]);

  // 5. Prepare Filter Options
  const filterOptions: StandardColumnOptions = useMemo(() => {
    // Extract task type options from countsData.types (contextual to vertical)
    // Use t.id as value for ID-based filtering (canonical_type_ids)
    const taskTypeOptions =
      staticMetadata?.static_groups_and_types?.types?.map((t) => ({
        label: t.type,
        value: t.id,
      })) || [];

    if (!staticMetadata?.filter_options) {
      return {
        firmOptions: [],
        firmStateOptions,
        incidentTypeOptions: [],
        caseStatusOptions: [],
        matterOptions: [],
        coverageOptions: [],
        assigneeOptions: [],
        priorityOptions: [],
        lastUpdateOptions: [],
        lastUpdateActorOptions: [],
        createdDateOptions: [],
        statusOptions: [],
        policeDepartmentOptions: [],
      medicalProviderOptions: [],
      taskTypeOptions,
      sourceOptions: [],
      agentStatusOptions: [],
      currentUserId: currentUser?.id,
    };
    }

    const options = staticMetadata.filter_options;
    return {
      firmOptions: options.firms || [],
      firmStateOptions,
      incidentTypeOptions: options.incident_types || [],
      caseStatusOptions: options.matter_statuses || [],
      matterOptions: options.matters || [],
      coverageOptions: options.coverage_carriers || options.coverages || [],
      // Sort assignees with presets first (unassigned, managing_paralegals, etc.)
      assigneeOptions: getSortedOptions(
        FILTER_CONFIG_KEYS.ASSIGNEE,
        options.assignees,
      ),
      priorityOptions: options.priorities || [],
      lastUpdateOptions: options.last_update_ranges || [],
      lastUpdateActorOptions: options.last_update_actors || [],
      createdDateOptions: options.created_date_ranges || [],
      statusOptions: options.task_statuses || [],
      policeDepartmentOptions: options.police_departments || [],
      medicalProviderOptions: options.medical_providers || [],
      taskTypeOptions,
      sourceOptions: options.task_sources || [],
      agentStatusOptions: options.agent_statuses || [],
      currentUserId: currentUser?.id,
    };
  }, [staticMetadata, currentUser?.id, firmStateOptions]);

  // 4. Create Columns
  const columns = useMemo(
    () => columnsFactory(filterOptions),
    [filterOptions, columnsFactory],
  );

  // 5. Prepare Counts
  const countsData = staticMetadata?.static_groups_and_types;
  const staticTotalCount = useMemo(() => {
    if (!countsData?.groups) return 0;
    return countsData.groups.reduce((sum, group) => sum + group.count, 0);
  }, [countsData]);

  // 6. Prepare Advanced Filters Metadata
  const advancedFiltersMetadata: AdvancedFiltersMetadata = useMemo(
    () => ({
      groupCounts: countsData,
      // Spread all filter options (includes statusOptions)
      ...filterOptions,
    }),
    [countsData, filterOptions],
  );

  return {
    staticMetadata,
    columns,
    countsData,
    staticTotalCount,
    advancedFiltersMetadata,
  };
};
